import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWindowSize } from 'react-use';
import { Box, Grid, useTheme } from '@mui/material';
import { BuildingList, BuildingSearch, CrystalButton, Loading } from 'components';
import { ROUTES } from 'consts';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useAppDispatch, useAppSelector } from 'store/store.hook';
import { useBuildingItems } from './buildings.hook';
import { updateLayout } from 'store/slices/layout.slice';

interface BuildingsPageProps {}

export const BuildingsPage: React.FC<BuildingsPageProps> = () => {
  const dispatch = useAppDispatch();
  const { orgSlug, canCreateBuildings } = useAppSelector(selectCurrentUserInfo);
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const theme = useTheme();

  const [searchKeyword, setSearchKeyword] = React.useState('');
  const [filters, setFilters] = React.useState({
    city: '',
    state: '',
    name: '',
  });

  const [loading, buildingItems, allBuildingItems] = useBuildingItems({
    searchKeyword,
    city: filters.city,
    state: filters.state,
    account_name: filters.name,
  });

  useEffect(() => {
    dispatch(updateLayout({ headerTitle: 'Buildings' }));
  }, []);

  const handleFilterChange = (updated: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...updated }));
  };

  const openAddBuilding = () => {
    setTimeout(() => {
      navigate(`/${orgSlug}/${ROUTES.BUILDINGS}/new`);
    }, 500);
  };

  return loading ? (
    <Loading text="Loading buildings..." height={400} />
  ) : (
    <Box sx={{ padding: 3 }}>
      <Grid container rowSpacing={5} columnSpacing={3}>
        <Grid
          item
          xs={12}
          container
          spacing={2}
          alignItems={width < theme.breakpoints.values.md ? 'center' : 'flex-start'}
        >
          <Grid item xs={11} lg={6} sx={{ flex: 1 }}>
            <BuildingSearch
              searchKeyword={searchKeyword}
              onSearchChange={setSearchKeyword}
              filters={filters}
              onFiltersChange={handleFilterChange}
              buildings={allBuildingItems}
              shouldShowFilters
            />
          </Grid>

          {canCreateBuildings && (
            <Grid item container xs={1} lg={6} sx={{ justifyContent: 'flex-end' }}>
              <CrystalButton onClick={openAddBuilding} />
            </Grid>
          )}
        </Grid>

        <Grid item xs={12}>
          <BuildingList
            buildings={buildingItems}
            onBuildingItem={(buildingId) => {
              navigate(`/${orgSlug}/${ROUTES.BUILDINGS}/${buildingId}`);
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
